/**
 * Choosing one value in one field (ADR 0078; owner report, 2026-10-02: "an
 * auto populated list to filter as I type, and I click the one I want").
 *
 * - Focusing the field lists every option. Typing narrows the list to labels
 *   containing what's typed, anywhere in the label.
 * - A click (or ↑ ↓ and Enter) chooses. The choice stays in the field as a
 *   light-blue chip, like the Techs page's, and × clears it. The chip may take
 *   the field's whole width, and a label longer than that wraps rather than
 *   being cut off, so a resource's ID or a ticket's number stays visible
 *   (#285); the field grows to fit, and the text input moves under the chip
 *   when there's no room beside it.
 * - An option can be listed but not chosen (`disabled`), such as another
 *   tech's extension, greyed with its `detail` saying why.
 * - With `allowTyped`, what's typed can be chosen as it is, when no option
 *   has it ("Use “…”"). A function decides which typed values are offered.
 * - The list opens in the form's flow, under the field, so a scrolling panel
 *   never clips it.
 * - `invalid` marks the field for assistive technology when the choice needs
 *   attention, such as an Autotask resource that became inactive (#285); the
 *   caller says why beneath it, and names that text in `describedBy` so the
 *   reason is read with it.
 *
 * The WAI-ARIA combobox pattern: the input is the combobox, the list its
 * listbox, and the highlighted option its active descendant.
 *
 * From the keyboard (rc.18): the option the keyboard is on is plainly
 * highlighted, and the pointer moves it (#221); the list opens on the chosen
 * option, and the chip saying what's chosen describes the field (#242); text
 * typed but not chosen is cleared on leaving the field (#246); and the
 * highlighted option is kept in view (#256).
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

export interface ComboboxOption {
  value: string;
  label: string;
  // Muted, beside the label: when it was seen, or whose it is.
  detail?: string | undefined;
  disabled?: boolean;
}

export function Combobox({
  label,
  hideLabel = false,
  describedBy,
  options,
  value,
  onChange,
  loading = false,
  failed = false,
  disabled = false,
  invalid = false,
  allowTyped = false,
  placeholder = "Choose",
  loadingText = "Loading…",
  failedText = "Couldn't load the choices",
}: {
  label: string;
  // The label is read by assistive technology but not shown (a table's column says it).
  hideLabel?: boolean;
  // Ids of what describes the field, space-separated, as aria-describedby takes them.
  describedBy?: string;
  options: ComboboxOption[];
  // The chosen value, or "".
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
  failed?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  allowTyped?: boolean | ((typed: string) => boolean);
  placeholder?: string;
  loadingText?: string;
  failedText?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const chosen = value === "" ? undefined : (options.find((o) => o.value === value) ?? { value, label: value });
  const typed = query.trim();
  const needle = typed.toLowerCase();
  const matches = options.filter((o) => needle === "" || o.label.toLowerCase().includes(needle));
  const typedOk = typeof allowTyped === "function" ? allowTyped(typed) : allowTyped;
  const offerTyped =
    typedOk && typed !== "" && !options.some((o) => o.value.toLowerCase() === needle);
  const listed: (ComboboxOption & { typed?: boolean })[] = offerTyped
    ? [...matches, { value: typed, label: `Use “${typed}”`, typed: true }]
    : matches;
  const choosable = listed.filter((o) => !o.disabled);
  const highlighted = open ? choosable[Math.min(active, choosable.length - 1)] : undefined;
  const optionId = (option: ComboboxOption & { typed?: boolean }) =>
    `${listId}-${option.typed ? "typed" : String(listed.indexOf(option))}`;
  const chipId = `${inputId}-chosen`;
  const description = [describedBy, chosen ? chipId : undefined].filter(Boolean).join(" ") || undefined;
  const highlightedId = highlighted ? optionId(highlighted) : undefined;

  // Opening: on the chosen option when it's listed, else the first (#242).
  function openList() {
    if (!open) {
      const at = chosen ? choosable.findIndex((o) => o.value === chosen.value) : -1;
      setActive(at >= 0 ? at : 0);
      setOpen(true);
    }
  }

  // The option the keyboard is on stays in view in a long list (#256).
  useEffect(() => {
    if (highlightedId) {
      document.getElementById(highlightedId)?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedId]);

  function choose(option: ComboboxOption) {
    onChange(option.value);
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => Math.max(0, Math.min(choosable.length - 1, current + step)));
    } else if (event.key === "Enter" && (open || query !== "" || highlighted !== undefined)) {
      // Choosing, never submitting the form around it while something is
      // typed or the list is open: with nothing to choose, the form would go
      // with the old value (a block logged to the old ticket, #223).
      event.preventDefault();
      if (highlighted !== undefined) {
        choose(highlighted);
      }
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  const shown = failed
    ? failedText
    : loading
      ? loadingText
      : chosen
        ? "Type to change"
        : placeholder;

  return (
    <div className="flex flex-col gap-[5px]">
      <label htmlFor={inputId} className={hideLabel ? "sr-only" : "text-xs font-semibold text-muted"}>
        {label}
      </label>
      {/* 40 px tall on one line, as before; taller only when the chosen label wraps (#285). */}
      <div className="flex min-h-10 flex-wrap items-center gap-x-1.5 gap-y-1 rounded-input border border-line bg-card py-[3px] pr-2 pl-1.5 focus-within:border-brand">
        {chosen ? (
          // 12 px corners: a pill on one line, and still a rounded chip, not a
          // capsule, when the label wraps.
          <span className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-input bg-brand-tint py-0.5 pr-1 pl-2.5 text-sm text-brand-ink-on-tint">
            <span id={chipId} className="min-w-0 break-words">
              <span className="sr-only">Chosen: </span>
              {chosen.label}
            </span>
            <button
              type="button"
              aria-label={`Clear ${chosen.label}`}
              disabled={disabled}
              className="shrink-0 cursor-pointer rounded-pill px-1 hover:bg-card disabled:cursor-not-allowed"
              onClick={() => {
                onChange("");
                // Back in the field, every option is listed again.
                inputRef.current?.focus();
              }}
            >
              ×
            </button>
          </span>
        ) : null}
        <input
          ref={inputRef}
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={description}
          aria-activedescendant={highlightedId}
          aria-invalid={invalid ? true : undefined}
          autoComplete="off"
          disabled={disabled || loading || failed}
          value={query}
          placeholder={shown}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={openList}
          onClick={openList}
          onBlur={() => {
            setOpen(false);
            // Typed but not chosen: cleared, so it can't look chosen (#246).
            setQuery("");
          }}
          onKeyDown={onKeyDown}
          // At least 4rem beside the chip, else on its own line under it.
          className="h-8 min-w-16 grow basis-16 bg-transparent px-1.5 text-[13.5px] text-text outline-none placeholder:text-muted"
        />
        <span aria-hidden="true" className="text-xs text-muted">
          ▾
        </span>
      </div>
      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={`${label} choices`}
          className="max-h-64 overflow-y-auto rounded-card border border-line bg-panel p-1.5"
        >
          {listed.map((option) => (
            <li
              key={option.typed ? "typed" : option.value}
              id={optionId(option)}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled ? true : undefined}
              // Keeps the input focused, so the click lands before the blur closes the list.
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onMouseEnter={() => {
                const at = choosable.indexOf(option);
                if (at >= 0) {
                  setActive(at);
                }
              }}
              onClick={() => {
                if (!option.disabled) {
                  choose(option);
                }
              }}
              className={
                "flex items-center justify-between gap-2 rounded-row px-3 py-2 text-sm " +
                (option.disabled ? "cursor-not-allowed text-muted " : "cursor-pointer ") +
                (option.value === value ? "font-semibold " : "") +
                // Plainly the one Enter would choose (#221), not a faint grey.
                (option === highlighted
                  ? "bg-brand-tint text-brand-ink-on-tint"
                  : option.value === value
                    ? "text-brand-ink"
                    : option.disabled
                      ? ""
                      : "text-text")
              }
            >
              <span>{option.label}</span>
              {option.detail ? (
                <span className="text-xs text-muted">
                  <span className="sr-only">, </span>
                  {option.detail}
                </span>
              ) : null}
            </li>
          ))}
          {listed.length === 0 ? (
            <li role="presentation" className="px-3 py-2 text-sm text-muted">
              {`Nothing matches “${typed}”`}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
